// Barra de ações do Baiak (1 a 12): magias, runas e poções. A caça automática usa a barra da esquerda
// para a direita, conforme as condições definidas no Auto Helper. Clique ou F1–F12 usam na hora.
import { useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { slot, type HotbarSlot } from '../../../../shared/src/game/helper';
import type { ItemInstance } from '../../../../shared/src/game/items';
import { spellByWords, spellsNow } from '../spells';
import { SpellIcon } from './SpellIcon';
import { ItemImg } from './ItemSlot';
import { catalogNow } from '../items';
import { POTIONS, potionBlock, potionOf } from '../../../../shared/src/game/combat';
import type { SpellDef } from '../../../../shared/src/game/spells';
import { itemCost, potionPriceByName, shortGp } from '../potionPrice';
import { CdOverlay } from './Cooldown';
import { SlotDetails } from './SlotTip';

export function slotLabel(sl: HotbarSlot): string {
  if ('spell' in sl.use) { const s = spellByWords(sl.use.spell); return s ? `${s.name} (${s.words})` : sl.use.spell; }
  return catalogNow()?.get(sl.use.item)?.name ?? 'Item';
}

export function ActionBar(props: {
  slots: Array<HotbarSlot | null>; items: ItemInstance[]; auto: boolean; inHunt: boolean;
  onAuto: () => void; onGambits: () => void; onChange: (slots: Array<HotbarSlot | null>) => void; onUse: (sl: HotbarSlot) => void;
  /** celular: 2 fileiras de 6 espaços grandes; toque usa, toque longo abre as opções, espaço vazio abre o Auto Helper */
  mobile?: boolean;
  /** celular: vocation/level/magic level para a lista de escolha do espaço */
  me?: { vocation: string; level: number; ml: number };
  /** quantas barras aparecem (PC: 1 ou 2 de 12; celular: 1 a 3 fileiras de 6), escolhidas pelo jogador nas setas */
  bars?: number; onBars?: (n: number) => void;
}) {
  const [pick, setPick] = useState<number | null>(null);
  const [tip, setTip] = useState<{ i: number; x: number; y: number } | null>(null);   // detalhes do espaço com o mouse em cima
  const tipT = useRef(0);
  const pickAt = useRef(0);   // celular: o dedo que segurou ainda está na tela quando o menu abre; o soltar não pode apertar um botão do menu
  const [choose, setChoose] = useState<number | null>(null);   // celular: escolhendo o que vai neste espaço
  // celular: item arrastado com o dedo até um espaço da barra (poção ou runa)
  const barRef = useRef<HTMLDivElement | null>(null);
  const slotsRef = useRef(props.slots); slotsRef.current = props.slots;
  const itemsRef = useRef(props.items); itemsRef.current = props.items;
  const changeRef = useRef(props.onChange); changeRef.current = props.onChange;
  useEffect(() => {
    const el = barRef.current; if (!el) return;
    const h = (e: Event) => {
      const i = Number((e.target as HTMLElement).dataset?.slot);
      const it = itemsRef.current.find((x) => x.id === (e as CustomEvent<{ id: number }>).detail.id);
      if (!Number.isInteger(i) || !it) return;
      const name = catalogNow()?.get(it.typeId)?.name.toLowerCase() ?? '';
      const n = [...slotsRef.current]; n[i] = slot({ item: it.typeId }, { stat: name.includes('mana') ? 'mana' : 'hp', below: 50 }); changeRef.current(n);
    };
    el.addEventListener('nxdrop', h);
    return () => el.removeEventListener('nxdrop', h);
  }, [props.mobile]);
  const press = useRef<{ i: number; t: number; long: boolean } | null>(null);
  // celular: a barra inteira encolhe para caber na largura da tela (sem rolar para o lado)
  const BAR_W = 600;   // 12 espaços de 36 px + os botões Caçar e Auto Helper
  const calc = () => (typeof window !== 'undefined' && window.innerWidth <= 860 ? Math.min(1, (window.innerWidth - 6) / BAR_W) : 1);
  const [fit, setFit] = useState(calc);
  useEffect(() => { const on = () => setFit(calc()); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on); }, []);
  // só o que está com o personagem (depot e Store Box não contam: precisam ir para a mochila)
  const byId = new Map(props.items.map((i) => [i.id, i]));
  const carried = (i: ItemInstance) => { let c: ItemInstance | undefined = i; while (c && c.place === 'container') c = c.parentId ? byId.get(c.parentId) : undefined; return !!c && (c.place === 'equip' || c.place === 'pouch'); };
  const count = (id: number) => props.items.filter((i) => i.typeId === id && carried(i)).reduce((a, i) => a + i.count, 0);
  const set = (i: number, v: HotbarSlot | null) => { const n = [...props.slots]; n[i] = v; props.onChange(n); };
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
      const m = /^F(\d{1,2})$/.exec(e.key);
      if (!m) return;
      const i = Number(m[1]) - 1 + (e.shiftKey ? 12 : 0);   // Shift+F1..F12: segunda barra
      if (i < 0 || i > 23 || (i > 11 && (props.bars ?? 1) < 2)) return;
      e.preventDefault();
      const sl = props.slots[i];
      if (sl) props.onUse(sl);
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [props.slots, props.bars]);
  if (props.mobile) {
    const start = (i: number) => {
      const t = window.setTimeout(() => { if (press.current) { press.current.long = true; pickAt.current = Infinity; setPick(i);
        // enquanto o dedo que segurou não sair da tela (e um instante depois), cliques no menu são ignorados
        const up = () => { pickAt.current = Date.now(); window.removeEventListener('pointerup', up, true); window.removeEventListener('touchend', up, true); };
        window.addEventListener('pointerup', up, true); window.addEventListener('touchend', up, true);
      } }, 450);
      press.current = { i, t, long: false };
    };
    const end = (i: number) => {
      const p = press.current; press.current = null;
      if (!p || p.i !== i) return;
      window.clearTimeout(p.t);
      if (p.long) return;
      const sl = props.slots[i];
      if (sl) props.onUse(sl); else setChoose(i);
    };
    const cancel = () => { if (press.current) window.clearTimeout(press.current.t); press.current = null; };
    const ps = pick !== null ? props.slots[pick] ?? null : null;
    const rows = Math.max(1, Math.min(3, props.bars ?? 2));
    return (
      <div class="m-hb-wrap">
      <div class="m-hotbar" aria-label="Barra de magias" ref={barRef}>
        {Array.from({ length: rows * 6 }, (_, i) => {
          const sl = props.slots[i] ?? null;
          const sp = sl && 'spell' in sl.use ? spellByWords(sl.use.spell) : undefined;
          const itemId = sl && 'item' in sl.use ? sl.use.item : undefined;
          return (
            <button key={i} data-nxdrop="1" data-slot={i} class={'m-hb' + (sl ? '' : ' empty') + (sl && !sl.auto ? ' manual' : '')}
              onPointerDown={() => start(i)} onPointerUp={() => end(i)} onPointerLeave={cancel} onPointerCancel={cancel}
              onContextMenu={(e) => { e.preventDefault(); }}>
              {sp && <SpellIcon spell={sp} size={32} />}
              {itemId !== undefined && <><ItemImg typeId={itemId} count={1} />{itemCost(itemId) !== undefined ? <span class="ab-count ab-cost" title="Custo por uso (gp do banco)">{shortGp(itemCost(itemId)!)}</span> : <span class="ab-count">{count(itemId)}</span>}</>}
              {!sl && <span class="m-hb-plus">+</span>}
              {sl && !sl.auto && <span class="ab-manual">M</span>}
              <CdOverlay slot={sl} />
            </button>
          );
        })}
        {choose !== null && props.me && createPortal(
          <SlotPicker me={props.me} slotNo={choose} current={props.slots} onClose={() => setChoose(null)}
            onPick={(use, o) => { set(choose, slot(use, o)); setChoose(null); }} />, document.body)}
        {pick !== null && createPortal(
          <div class="m-hb-menu-bg" onClickCapture={(e) => { if (pickAt.current === Infinity || Date.now() - pickAt.current < 350) { e.stopPropagation(); e.preventDefault(); } }} onClick={() => setPick(null)}>
            <div class="m-hb-menu" onClick={(e) => e.stopPropagation()}>
              {ps ? <SlotDetails slot={ps} count={'item' in ps.use ? count(ps.use.item) : undefined} /> : <b>{`Espaço ${pick + 1}`}</b>}
              {ps && isHealSlot(ps) && (
                <div class="m-hb-pct">
                  <span>Usar quando {ps.stat === 'mana' ? 'a mana' : 'a vida'} estiver abaixo de</span>
                  <span class="num-step big">
                    <button type="button" disabled={ps.below <= 5} onClick={() => set(pick, { ...ps, below: Math.max(5, ps.below - 5) })}>−</button>
                    <b>{ps.below}%</b>
                    <button type="button" disabled={ps.below >= 100} onClick={() => set(pick, { ...ps, below: Math.min(100, ps.below + 5) })}>+</button>
                  </span>
                </div>
              )}
              {ps && <button class="tb-button green" onClick={() => { props.onUse(ps); setPick(null); }}>Usar</button>}
              {ps && <button class="tb-button" onClick={() => { set(pick, { ...ps, auto: !ps.auto }); setPick(null); }}>{ps.auto ? 'Só manual (fora da caça automática)' : 'Usar na caça automática'}</button>}
              <button class="tb-button" onClick={() => { setChoose(pick); setPick(null); }}>Trocar o que vai aqui</button>
              <button class="tb-button" onClick={() => { setPick(null); props.onGambits(); }}>Configurar no Auto Helper</button>
              {ps && <button class="tb-button" onClick={() => { set(pick, null); setPick(null); }}>Tirar da barra</button>}
              <button class="tb-button" onClick={() => setPick(null)}>Fechar</button>
            </div>
          </div>, document.body)}
      </div>
      {props.onBars && (
        <span class="m-hb-rows" title="Fileiras da barra de magias">
          <button type="button" aria-label="Mais uma fileira" disabled={rows >= 3} onClick={() => props.onBars!(rows + 1)}>▲</button>
          <button type="button" aria-label="Uma fileira a menos" disabled={rows <= 1} onClick={() => props.onBars!(rows - 1)}>▼</button>
        </span>
      )}
      </div>
    );
  }
  const rowsD = Math.max(1, Math.min(2, props.bars ?? 1));
  const slotEl = (i: number) => {
        const sl = props.slots[i] ?? null;
        const sp = sl && 'spell' in sl.use ? spellByWords(sl.use.spell) : undefined;
        const itemId = sl && 'item' in sl.use ? sl.use.item : undefined;
        void spellsNow;
        return (
          <span key={i} data-nxdrop="1" data-slot={i} class={'ab-slot' + (sl && !sl.auto ? ' manual' : '')} title={sl ? undefined : `Espaço ${i + 1}: arraste uma magia, runa ou poção`}
            onMouseEnter={(e) => { if (!sl) return; const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); clearTimeout(tipT.current); tipT.current = window.setTimeout(() => setTip({ i, x: r.left + r.width / 2, y: r.top }), 250); }}
            onMouseLeave={() => { clearTimeout(tipT.current); setTip(null); }}
            onClick={() => sl && props.onUse(sl)}
            onContextMenu={(e) => { e.preventDefault(); if (sl) set(i, null); }}
            draggable={!!sl} onDragStart={(e) => e.dataTransfer!.setData('application/x-noxara-slot', String(i))}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const dt = e.dataTransfer!;
              const from = dt.getData('application/x-noxara-slot');
              if (from !== '') { const n = [...props.slots]; const a = Number(from); [n[a], n[i]] = [n[i] ?? null, n[a] ?? null]; props.onChange(n); return; }
              const sp = dt.getData('application/x-noxara-spell');
              if (sp) { set(i, slot(sp.startsWith('s:') ? { spell: sp.slice(2) } : { item: Number(sp.slice(2)) }, { stat: 'hp', below: 60 })); return; }
              const instId = Number(dt.getData('text/plain'));
              const it = props.items.find((x) => x.id === instId);
              if (it) {
                const name = catalogNow()?.get(it.typeId)?.name.toLowerCase() ?? '';
                set(i, slot({ item: it.typeId }, { stat: name.includes('mana') ? 'mana' : 'hp', below: 50 }));
              }
            }}>
            {sp && <SpellIcon spell={sp} size={32} />}
            {itemId !== undefined && <><ItemImg typeId={itemId} count={1} />{itemCost(itemId) !== undefined ? <span class="ab-count ab-cost" title="Custo por uso (gp do banco)">{shortGp(itemCost(itemId)!)}</span> : <span class="ab-count">{count(itemId)}</span>}</>}
            {sl && !sl.auto && <span class="ab-manual">M</span>}
            <CdOverlay slot={sl} />
            <small>{i < 12 ? i + 1 : `⇧${i - 11}`}</small>
            {tip?.i === i && sl && createPortal(
              <div class="st-tip" style={{ left: Math.max(150, Math.min(window.innerWidth - 150, tip.x)) + 'px', top: tip.y - 6 + 'px' }}>
                <SlotDetails slot={sl} count={itemId !== undefined ? count(itemId) : undefined} keyHint={`Clique ou ${i < 12 ? `F${i + 1}` : `Shift+F${i - 11}`} para usar · botão direito tira da barra`} />
              </div>, document.body)}
          </span>
        );
  };
  return (
    <div class={'actionbar' + (rowsD > 1 ? ' ab-two' : '')} aria-label="Barra de ações" ref={barRef} style={fit < 1 ? { zoom: fit } : undefined}>
      <div class="ab-rows">
        {Array.from({ length: rowsD }, (_, r) => rowsD - 1 - r).map((r) => <div key={r} class="ab-row">{Array.from({ length: 12 }, (_, k) => slotEl(r * 12 + k))}</div>)}
      </div>
      {props.onBars && (
        <span class="ab-bars" title="Barras de ações (até 2)">
          <button type="button" aria-label="Mais uma barra" disabled={rowsD >= 2} onClick={() => props.onBars!(rowsD + 1)}>▲</button>
          <button type="button" aria-label="Uma barra a menos" disabled={rowsD <= 1} onClick={() => props.onBars!(rowsD - 1)}>▼</button>
        </span>
      )}
      <button class={'txt-btn ab-auto' + (props.auto ? ' on' : '')} disabled={!props.inHunt} onClick={props.onAuto}
        title={props.inHunt ? 'Liga ou desliga a caça automática' : 'Entre numa hunt para caçar'}>
        {props.auto ? 'Caçando' : 'Caçar'}
      </button>
      <button class="txt-btn ab-gambits" title="Auto Helper: curas, ataques, suporte, condições, monstros especiais e loot" onClick={props.onGambits}>Auto Helper</button>
    </div>
  );
}

/** poção ou cura (magia/runa de cura): o espaço tem a porcentagem de vida/mana para usar */
function isHealSlot(sl: HotbarSlot): boolean {
  if ('item' in sl.use) { const n = catalogNow()?.get(sl.use.item)?.name ?? ''; return !!potionOf(n) || spellsNow().some((x) => x.kind === 'rune' && x.item === (sl.use as { item: number }).item && x.group === 'healing'); }
  return spellByWords(sl.use.spell)?.group === 'healing';
}

// --- celular: escolher o que vai num espaço da barra, em abas e da melhor opção disponível para a pior ---
type PickTab = 'potion' | 'heal' | 'attack' | 'rune' | 'support';
const PICK_TABS: Array<[PickTab, string, string]> = [['potion', 'Poções', '🧪'], ['heal', 'Cura', '❤️'], ['attack', 'Ataque', '⚔️'], ['rune', 'Runas', '🔮'], ['support', 'Suporte', '✨']];
interface PickOpt { key: string; name: string; sub: string; use: { spell: string } | { item: number }; lock: string | null; score: number; o: Partial<HotbarSlot>; spell?: SpellDef; item?: number }

function pickOptions(tab: PickTab, me: { vocation: string; level: number; ml: number }): PickOpt[] {
  const cat = catalogNow();
  const out: PickOpt[] = [];
  if (tab === 'potion') {
    for (const [name, p] of Object.entries(POTIONS)) {
      const t = cat && [...cat.values()].find((x) => x.name === name);
      if (!t) continue;
      const mana = p.kind === 'mana';
      out.push({ key: 'i' + t.id, name, item: t.id, use: { item: t.id }, lock: potionBlock(p, me.vocation, me.level),
        sub: `${p.min}–${p.max} de ${mana ? 'mana' : 'vida'}${p.mmin ? ` e ${p.mmin}–${p.mmax} de mana` : ''}${p.level ? ` · level ${p.level}` : ''}${potionPriceByName(name) ? ` · ${potionPriceByName(name)} gp por uso` : ''}`,
        score: p.max + (p.mmax ?? 0), o: mana ? { stat: 'mana', below: 40 } : { stat: 'hp', below: 50 } });
    }
  } else {
    for (const sp of spellsNow()) {
      if (!sp.usable) continue;
      const rune = sp.kind === 'rune';
      const fits = tab === 'heal' ? sp.group === 'healing'
        : tab === 'attack' ? !rune && sp.group === 'attack'
        : tab === 'rune' ? rune && (sp.group === 'attack' || sp.group === 'field' || sp.group === 'support')
        : !rune && (sp.group === 'support' || sp.group === 'party');
      if (!fits) continue;
      if (!rune && !sp.vocations.includes(me.vocation as never)) continue;
      const lock = rune ? (me.ml < sp.ml ? `magic level ${sp.ml}` : null) : me.level < sp.level ? `level ${sp.level}` : null;
      out.push({ key: (rune ? 'r' : 's') + sp.words, name: sp.name, spell: sp, use: rune && sp.item ? { item: sp.item } : { spell: sp.words }, lock,
        sub: rune ? `Runa · ML ${sp.ml}${sp.price ? ` · ${sp.price} gp por uso` : ''}` : `Level ${sp.level} · ${sp.mana} de mana`,
        score: rune ? sp.ml * 1000 + (sp.price ?? 0) : sp.level * 1000 + sp.mana, o: sp.group === 'healing' ? { stat: 'hp', below: 60 } : {} });
    }
  }
  // as que você pode usar primeiro, da melhor para a pior; depois as bloqueadas, da mais perto de liberar
  return out.sort((a, b) => (a.lock ? 1 : 0) - (b.lock ? 1 : 0) || (a.lock ? a.score - b.score : b.score - a.score));
}

function SlotPicker(props: { me: { vocation: string; level: number; ml: number }; slotNo: number; current: Array<HotbarSlot | null>;
  onPick: (use: { spell: string } | { item: number }, o: Partial<HotbarSlot>) => void; onClose: () => void }) {
  const [tab, setTab] = useState<PickTab>('potion');
  const list = pickOptions(tab, props.me);
  const inBar = (o: PickOpt) => props.current.some((s) => s && ('spell' in o.use ? 'spell' in s.use && s.use.spell === o.use.spell : 'item' in s.use && s.use.item === (o.use as { item: number }).item));
  return (
    <div class="m-hb-menu-bg" onClick={props.onClose}>
      <div class="spk" onClick={(e) => e.stopPropagation()}>
        <div class="spk-head"><b>Espaço {props.slotNo + 1}: escolha o que usar</b><button onClick={props.onClose} aria-label="Fechar">✕</button></div>
        <div class="spk-tabs">
          {PICK_TABS.map(([id, label, ico]) => <button key={id} class={tab === id ? 'on' : ''} onClick={() => setTab(id)}><i>{ico}</i>{label}</button>)}
        </div>
        <div class="spk-list">
          {list.map((o, n) => (
            <button key={o.key} class={'spk-opt' + (o.lock ? ' locked' : '') + (!o.lock && n === 0 ? ' best' : '')} disabled={!!o.lock} onClick={() => props.onPick(o.use, o.o)}>
              <span class="spk-ico">{o.spell ? <SpellIcon spell={o.spell} size={32} /> : o.item !== undefined ? <ItemImg typeId={o.item} count={1} /> : null}</span>
              <span class="spk-txt"><b>{o.name}</b><small>{o.sub}</small></span>
              <span class="spk-tag">{o.lock ? `🔒 ${o.lock}` : inBar(o) ? 'na barra' : !o.lock && n === 0 ? 'melhor' : ''}</span>
            </button>
          ))}
          {!list.length && <p class="muted small">Nada nesta aba para a sua vocation.</p>}
        </div>
      </div>
    </div>
  );
}
